/**
 * Floating team chat widget — always visible in the bottom-right corner.
 * Click the bubble to open the panel; panel swaps between channel list
 * and message thread views.
 */

import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { listChannels, listOnlineUsers, listStaff } from '@/api/team'
import { useTeamStream } from '@/hooks/useTeamStream'
import { useTeamStore, useTotalUnread } from '@/store/team'
import { useAuthStore } from '@/store/auth'
import { ChannelList } from './ChannelList'
import { MessageThread } from './MessageThread'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

export function TeamChatWidget() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated())
  const currentUserId = useAuthStore((s) => s.user?.id)

  const panelOpen = useTeamStore((s) => s.panelOpen)
  const togglePanel = useTeamStore((s) => s.togglePanel)
  const activeChannelId = useTeamStore((s) => s.activeChannelId)
  const goBack = useTeamStore((s) => s.goBack)
  const channels = useTeamStore((s) => s.channels)
  const setChannels = useTeamStore((s) => s.setChannels)
  const setUserOnline = useTeamStore((s) => s.setUserOnline)
  const totalUnread = useTotalUnread()
  // The inbox pins its composer to the bottom edge, where this launcher
  // would cover Send until the details pane appears at xl. Hide it there;
  // team chat stays reachable from the avatar menu.
  const { pathname } = useLocation()
  const hideLauncher = pathname.startsWith('/inbox')

  // Connect WebSocket
  const { connected } = useTeamStream()

  // Fetch initial channels
  useQuery({
    queryKey: ['team-channels-init'],
    queryFn: async () => {
      const res = await listChannels()
      setChannels(res.channels)
      return res.channels
    },
    staleTime: 30_000,
    enabled: isAuthenticated,
  })

  // Fetch initial online users
  useQuery({
    queryKey: ['team-online-init'],
    queryFn: async () => {
      const res = await listOnlineUsers()
      for (const uid of res.online) setUserOnline(uid, true)
      return res.online
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
    enabled: isAuthenticated,
  })

  // Staff directory for name resolution
  const staffQ = useQuery({
    queryKey: ['team-staff-widget'],
    queryFn: async () => {
      const res = await listStaff()
      return new Map(res.staff.map((s) => [s.id, s]))
    },
    staleTime: 60_000,
    enabled: isAuthenticated,
  })
  const staffMap = staffQ.data ?? new Map()

  if (!isAuthenticated) return null

  // Resolve channel name for thread view
  const activeChannel = channels.find((c) => c.id === activeChannelId)
  const resolveChannelName = () => {
    if (!activeChannel) return ''
    if (activeChannel.kind === 'group') return `# ${activeChannel.name || 'channel'}`
    // DM — find the other member and show their name
    const otherId = activeChannel.members.find((m) => m !== currentUserId)
    if (!otherId) return 'DM'
    const staff = staffMap.get(otherId)
    return staff?.display_name || staff?.email?.split('@')[0] || 'Chat'
  }
  const channelName = resolveChannelName()

  return (
    <>
      {/* Floating button */}
      <button
        onClick={togglePanel}
        className={cn(
          // z-40: sits under dialogs, the nav drawer and the command palette (z-50).
          'fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-40 w-12 h-12 rounded-full',
          hideLauncher && !panelOpen && 'max-xl:hidden',
          ' flex items-center justify-center shadow-lg transition-all cursor-pointer',
          panelOpen
            ? 'bg-ink-700 hover:bg-ink-600'
            : 'bg-primary hover:bg-primary-hover',
          totalUnread > 0 && !panelOpen && 'animate-bounce',
        )}
        title="Team chat"
      >
        <Icon
          name={panelOpen ? 'xmark' : 'comments'}
          variant="solid"
          className="text-white text-lg"
        />
        {totalUnread > 0 && (
          <>
            {/* Pulse ring */}
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-danger animate-ping opacity-75" />
            {/* Badge */}
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center px-1">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          </>
        )}
      </button>

      {/* Backdrop — click outside to close */}
      {panelOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={togglePanel}
        />
      )}

      {/* Panel */}
      {panelOpen && (
        <div
          className={cn(
            'fixed bottom-20 inset-x-2 sm:inset-x-auto sm:right-5 z-50 sm:w-[380px] rounded-xl border border-hairline bg-surface shadow-2xl overflow-hidden flex flex-col',
            'animate-in fade-in slide-in-from-bottom-2 duration-200',
          )}
          style={{ height: 'min(520px, calc(100dvh - 120px))' }}
        >
          {activeChannelId ? (
            <MessageThread
              channelId={activeChannelId}
              channelName={channelName}
              onBack={goBack}
            />
          ) : (
            <ChannelList />
          )}

          {/* Connection indicator */}
          {!connected && (
            <div className="absolute top-0 left-0 right-0 bg-warn/90 text-white text-[10px] text-center py-0.5">
              Reconnecting...
            </div>
          )}
        </div>
      )}
    </>
  )
}
